import { QuestionAnswerDto } from 'api/borrower';
import * as Yup from 'yup';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { CivilityCodes } from 'shared/utils/civility';

// Field Names for minimal forms
export const MinimalBorrowerFormFieldNames = {
  // Natural Person fields
  LastName: PersonalInformationFieldNames.LastName,
  FirstName: PersonalInformationFieldNames.FirstName,
  Email: PersonalInformationFieldNames.Email,
  BirthDate: PersonalInformationFieldNames.BirthDate,
  // Legal Entity fields
  CompanyName: LegalEntityFormFieldNames.CompanyName,
  // Address fields
  AddressSearch: 'addressSearch',
  AdditionalAddress: 'address.additionalAddress',
  // Outstanding amounts
  ExistingOutstanding: 'existingOutstanding',
  // Common fields
  BorrowerId: 'borrowerId',
  SurveyAnswers: 'surveyAnswers'
} as const;

export type MinimalBorrowerFormFieldNames =
  (typeof MinimalBorrowerFormFieldNames)[keyof typeof MinimalBorrowerFormFieldNames];

export interface AddressFormValues {
  address?: string;
  additionalAddress?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  googlePlaceId?: string;
  latitude?: number;
  longitude?: number;
}

// Base form values (common to both types)
export interface MinimalBorrowerBaseFormValues {
  borrowerId?: string;
  surveyAnswers: QuestionAnswerDto[];
}

// Natural Person Form Values
export interface MinimalNaturalPersonFormValues extends MinimalBorrowerBaseFormValues {
  id?: string;
  [PersonalInformationFieldNames.Civility]: string;
  [PersonalInformationFieldNames.LastName]: string;
  [PersonalInformationFieldNames.FirstName]: string;
  [PersonalInformationFieldNames.Email]: string;
  [PersonalInformationFieldNames.BirthDate]: string;
  addressSearch?: string;
  address?: AddressFormValues;
  existingOutstanding?: number;
}

// Legal Entity Form Values
export interface MinimalLegalEntityFormValues extends MinimalBorrowerBaseFormValues {
  id?: string;
  [LegalEntityFormFieldNames.CompanyName]: string;
  [PersonalInformationFieldNames.Email]: string;
}

// Union type for both form types
export type MinimalBorrowerFormValues =
  | MinimalNaturalPersonFormValues
  | MinimalLegalEntityFormValues;

// Validation schema for Natural Person
export const minimalNaturalPersonValidationSchema = Yup.object({
  [PersonalInformationFieldNames.Civility]: Yup.string().required(
    'BORROWER_INSURED_FORM.VALIDATION.REQUIRED'
  ),
  [PersonalInformationFieldNames.LastName]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .max(50, 'BORROWER_INSURED_FORM.VALIDATION.MAX_LENGTH'),
  [PersonalInformationFieldNames.FirstName]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .max(50, 'BORROWER_INSURED_FORM.VALIDATION.MAX_LENGTH'),
  [PersonalInformationFieldNames.Email]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .email('BORROWER_INSURED_FORM.VALIDATION.EMAIL')
    .max(254, 'BORROWER_INSURED_FORM.VALIDATION.MAX_LENGTH'),
  [PersonalInformationFieldNames.BirthDate]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .test('age', 'BORROWER_INSURED_FORM.VALIDATION.MIN_AGE_16', (value) => {
      if (!value) return false;
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      return age >= 16;
    }),
  addressSearch: Yup.string()
    .test(
      'google-place-selected',
      'BORROWER_INSURED_FORM.VALIDATION.ADDRESS_SELECTION_REQUIRED',
      function () {
        // L'adresse doit avoir été choisie dans l'autocomplete Google : on exige
        // le place_id, pas seulement la ville (une donnée legacy peut avoir une
        // ville sans place_id et serait rejetée en 400 par le back).
        const { address } = this.parent;
        return !!(address && address.googlePlaceId);
      }
    )
});

// Validation schema for Legal Entity
export const minimalLegalEntityValidationSchema = Yup.object({
  [LegalEntityFormFieldNames.CompanyName]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .max(200, 'BORROWER_INSURED_FORM.VALIDATION.MAX_LENGTH'),
  [PersonalInformationFieldNames.Email]: Yup.string()
    .required('BORROWER_INSURED_FORM.VALIDATION.REQUIRED')
    .email('BORROWER_INSURED_FORM.VALIDATION.EMAIL')
    .max(254, 'BORROWER_INSURED_FORM.VALIDATION.MAX_LENGTH')
});

// Initial values for Natural Person
export const minimalNaturalPersonInitialValues: MinimalNaturalPersonFormValues =
  {
    id: undefined,
    borrowerId: undefined,
    [PersonalInformationFieldNames.Civility]: CivilityCodes.Mr,
    [PersonalInformationFieldNames.LastName]: '',
    [PersonalInformationFieldNames.FirstName]: '',
    [PersonalInformationFieldNames.Email]: '',
    [PersonalInformationFieldNames.BirthDate]: '',
    addressSearch: '',
    address: undefined,
    existingOutstanding: undefined,
    surveyAnswers: []
  };

// Initial values for Legal Entity
export const minimalLegalEntityInitialValues: MinimalLegalEntityFormValues = {
  id: undefined,
  borrowerId: undefined,
  [LegalEntityFormFieldNames.CompanyName]: '',
  [PersonalInformationFieldNames.Email]: '',
  surveyAnswers: []
};

// Form configs
export const minimalNaturalPersonFormConfig = {
  initialValues: minimalNaturalPersonInitialValues,
  validationSchema: minimalNaturalPersonValidationSchema
};

export const minimalLegalEntityFormConfig = {
  initialValues: minimalLegalEntityInitialValues,
  validationSchema: minimalLegalEntityValidationSchema
};
